import React from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import Styled from 'styled-components';

export const Gauge = ({
	min = 0,
	max = 100,
	value = 0,
	target,
	targetContent = '',
	valueText = '',
	affix,
	startAngle = -145,
	endAngle = 145,
	activeColor = '#88D6FF',
	width,
}) => {
	const degreesPerTick = (Math.abs(startAngle) + Math.abs(endAngle)) / (Math.abs(min) + Math.abs(max));
	const valueDegrees = value * degreesPerTick + startAngle;
	const targetDegrees = target * degreesPerTick + startAngle;
	const degreesToRadian = degrees => {
		return (Math.PI / 180) * degrees;
	};
	const backgroundArcPath = d3
		.arc()
		.innerRadius(0.7 * 200)
		.outerRadius(0.9 * 200)
		.startAngle(degreesToRadian(startAngle))
		.endAngle(degreesToRadian(endAngle))
		.cornerRadius(0.1 * 200)();

	const filledArcPath = d3
		.arc()
		.innerRadius(0.7 * 200)
		.outerRadius(0.9 * 200)
		.startAngle(degreesToRadian(startAngle))
		.endAngle(degreesToRadian(valueDegrees))
		.cornerRadius(0.1 * 200)();

	// Used to create a none rounde corner arc for the end of the filled arc
	const complimentaryFilledArcPath = d3
		.arc()
		.innerRadius(0.7 * 200)
		.outerRadius(0.9 * 200)
		.startAngle(degreesToRadian(startAngle - startAngle * 0.05))
		.endAngle(degreesToRadian(valueDegrees))();

	return (
		<GaugeContainer width={width}>
			<SVG width="100%" height="100%" viewBox={[-225, -200, 450, 400].join(' ')}>
				<defs>
					<filter id="f1" x="-90%" y="-90%" width="400%" height="400%" colorInterpolationFilters="sRGB">
						<feComponentTransfer in="SourceAlpha">
							<feFuncR type="discrete" tableValues="0.7" />
							<feFuncG type="discrete" tableValues="0.7" />
							<feFuncB type="discrete" tableValues="0.7" />
						</feComponentTransfer>
						<feGaussianBlur stdDeviation="20" />
						<feOffset dx="10" dy="10" result="shadow" />
						<feComposite in="SourceGraphic" in2="shadow" operator="over" />
					</filter>
				</defs>
				<path d={backgroundArcPath} fill="#e4e4e4" />
				<path d={filledArcPath} fill={activeColor} />
				{(100 / (Math.abs(min) + Math.abs(max))) * value > 5 && (
					<path d={complimentaryFilledArcPath} fill={activeColor} />
				)}
				{!!target && (
					<line
						transform={`rotate(${targetDegrees})`}
						strokeDasharray="10, 10"
						x1="0"
						y1="0"
						x2="0"
						y2="-190"
						stroke="#6b73ca"
						strokeWidth="6"
					/>
				)}
				<circle cx="0" cy="0" r="40" fill="#fff" filter="url(#f1)" />
				<text x="0" y="150" className="text" textAnchor="middle" fill="#535353" fontSize="60">
					{`${value}`}
					{!!affix && <tspan fontSize="40">{affix}</tspan>}
				</text>
				<g
					stroke="none"
					strokeWidth="1"
					fill="none"
					fillRule="evenodd"
					transform={`rotate(${valueDegrees}) translate(-200, -200)`}
				>
					<path
						d="M200.121257,7.5412075 C197.753287,7.45468566 195.759595,9.29966192 195.666558,11.6678802 L188.255672,200.308619 C188.249692,200.460838 188.246701,200.613159 188.246701,200.765495 C188.246701,207.094402 193.298362,212.24343 199.589467,212.400364 L199.958142,212.40405 C200.111395,212.40405 200.264634,212.401041 200.417769,212.395025 C206.879284,212.141179 211.911595,206.697304 211.65775,200.235789 L204.249725,11.6678802 C204.164566,9.50019389 202.479913,7.74861108 200.340919,7.55544212 L200.121257,7.5412075 Z"
						fill="#FFFFFF"
						fillRule="nonzero"
					/>
					<path
						d="M200.048229,9.53987378 C201.244919,9.59036596 202.204254,10.5497009 202.251267,11.7463911 L209.659292,200.3143 C209.869776,205.672097 205.697055,210.186081 200.339258,210.396566 C200.21228,210.401555 200.085217,210.40405 199.958142,210.40405 L199.885255,210.40405 C194.562029,210.40405 190.246701,206.088722 190.246701,200.765495 C190.246701,200.639337 190.249178,200.513191 190.25413,200.38713 L197.665017,11.7463911 C197.71477,10.4799331 198.781771,9.49359943 200.048229,9.53987378 Z M200,196 C197.790861,196 196,197.790861 196,200 C196,202.209139 197.790861,204 200,204 C202.209139,204 204,202.209139 204,200 C204,197.790861 202.209139,196 200,196 Z"
						fill="#535353"
					/>
				</g>
			</SVG>
			<LegendSection>
				{!!target && (
					<LegendContainer>
						<TargetLine />
						<LegendText>{targetContent}</LegendText>
					</LegendContainer>
				)}
				<LegendContainer>
					<LegendRectangle color={activeColor} />
					<LegendText>{valueText}</LegendText>
				</LegendContainer>
			</LegendSection>
		</GaugeContainer>
	);
};

Gauge.propTypes = {
	/**
	 * the minimum scale
	 */
	min: PropTypes.number,
	/**
	 * the maximum scale
	 */
	max: PropTypes.number,
	/**
	 * Value of the dial on the gauge
	 */
	value: PropTypes.number.isRequired,
	/**
	 * Optional target, to show a dotted line on the gauge
	 */
	target: PropTypes.number,
	/**
	 * text shown in the legent for the target
	 */
	targetContent: PropTypes.node,
	/**
	 * text shown in the legent for the value
	 */
	valueText: PropTypes.string,
	/**
	 * Optional changing the main color of the gauge
	 */
	activeColor: PropTypes.string,
	/**
	 * append a character or string of characters to the value
	 */
	affix: PropTypes.string,
	/**
	 * if using a high scale, the value displayed will collide with the arcs, so you can increase or decrease the start and end angle
	 */
	startAngle: PropTypes.number,
	/**
	 * if using a high scale, the value displayed will collide with the arcs, so you can increase or decrease the start and end angle
	 */
	endAngle: PropTypes.number,
	/**
	 * Width of the gauge container
	 */
	width: PropTypes.number,
};

const SVG = Styled.svg`
    text {
        font-family: "neue-haas-grotesk-text";
        font-weight: 600;
        color: ${props => props.theme.colors.v2_text_gray};
    }
`;

const GaugeContainer = Styled.div`
    display: inline-block;
	flex-direction: column;
	${props => (props.width ? `width: ${props.width}px` : '')}
`;

const LegendSection = Styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
`;

const LegendContainer = Styled.div`
	display: flex;
	&:not(:last-child) {
		margin-right: 24px;
	}
`;

const LegendRectangle = Styled.div`
	margin: auto 10px auto 0px;
    height: 12px;
	width: 24px;
	background-color: ${props => props.color || props.theme.colors.v2_grey}
`;

const TargetLine = Styled.div`    
	border: 2px dashed #2f61d5;
	height: 0px;
	width: 25px;
	margin: auto 10px auto 0px;
`;

const LegendText = Styled.p`
    color: ${props => props.theme.colors.v2_text_gray};
    margin: 0;
    padding: 0;
    font-size: 12px;
`;
